import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, isoDate } from "@/lib/format";
import { download } from "@/lib/accounting";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SCOPES = { IT: "Telefoni, SIM, PC e tablet", ALL: "Tutte le dotazioni" };

export const Handover = ({ emp }) => {
  const [list, setList] = useState([]);
  const [scope, setScope] = useState("IT");
  const [date, setDate] = useState(isoDate(new Date()));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => api.get(`/personnel/employees/${emp.id}/handovers`).then((r) => setList(r.data)).catch(() => {}), [emp.id]);
  useEffect(() => { load(); }, [load]);
  const name = `${emp.last_name}-${emp.first_name}`.replace(/\s+/g, "_");
  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Scegli il file del verbale firmato"); return; }
    const fd = new FormData();
    fd.append("file", f); fd.append("delivered_on", date); fd.append("scope", scope); fd.append("notes", notes);
    setBusy(true);
    try { await api.post(`/personnel/employees/${emp.id}/handovers`, fd); toast.success("Verbale firmato archiviato"); fileRef.current.value = ""; setNotes(""); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const del = async (h) => { if (!window.confirm("Eliminare il verbale archiviato?")) return; try { await api.delete(`/personnel/handovers/${h.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-4 border bg-muted/20 p-3" data-testid="handover-section">
      <div className="micro-label mb-2">Verbale di consegna</div>
      <div className="flex flex-wrap items-center gap-2">
        <select className="h-9 border bg-white px-2 text-sm" value={scope} onChange={(e) => setScope(e.target.value)} data-testid="handover-scope">{Object.entries(SCOPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <Button size="sm" variant="outline" onClick={() => download(`/personnel/employees/${emp.id}/handover-pdf`, { scope }, `verbale-dotazioni-${name}.pdf`)} data-testid="handover-pdf-button"><FileText className="mr-1 h-4 w-4" />Stampa verbale PDF</Button>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-[180px_1fr]">
        <DatePicker compact value={date} onChange={(v) => v && setDate(v)} testId="handover-date" />
        <Input className="h-9 bg-white" placeholder="Note (facoltative)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="handover-notes" />
        <input ref={fileRef} type="file" accept="application/pdf,image/*" className="text-sm sm:col-span-2" data-testid="handover-file" />
      </div>
      <Button size="sm" className="mt-2" disabled={busy} onClick={upload} data-testid="handover-upload-button"><Upload className="mr-1 h-4 w-4" />{busy ? "Caricamento…" : "Archivia verbale firmato"}</Button>
      <div className="mt-3" data-testid="handover-list">
        {list.length === 0 && <p className="text-sm text-muted-foreground">Nessun verbale firmato archiviato</p>}
        {list.map((h) => (
          <div key={h.id} className="flex items-center justify-between gap-2 border-b py-1.5 text-sm" data-testid={`handover-row-${h.id}`}>
            <span>Consegna del {fmtDate(h.delivered_on)} · {SCOPES[h.scope]} · {h.items.length} beni {h.notes && <span className="text-muted-foreground">· {h.notes}</span>}<span className="block text-xs text-muted-foreground">{h.filename} · caricato da {h.uploaded_by}</span></span>
            <span className="flex shrink-0">
              <Button size="icon" variant="ghost" onClick={() => download(`/personnel/handovers/${h.id}/file`, {}, h.filename)} data-testid={`handover-download-${h.id}`}><Download className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(h)} data-testid={`handover-delete-${h.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
